import { useIntl } from 'react-intl';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import usePagination from 'src/registry/hooks/usePagination';
import { createColumnHelper } from '@tanstack/react-table';
import {
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { commonMessages } from 'src/utils/messages';
import { CellLinkWithIcon } from 'src/registry/components/table/cells/CellLinkWithIcon';
import { useParams } from 'react-router-dom';
import useFilters from 'src/registry/hooks/useFilters';
import { useListBillingEntityBankAccounts } from 'src/features/billingEntityBankAccounts/api/useListBillingEntityBankAccounts';
import { bankAccountMessages } from 'src/features/billingEntityBankAccounts/utils/messages';
import { moneyAccountsMessages } from 'src/features/moneyAccounts/utils/messages';
import { BankAccount } from 'src/features/billingEntityBankAccounts/utils/types';
import { CellCurrencyBadge } from 'src/features/moneyAccounts/components/tables/CellCurrencyBadge';
import { CellBoolean } from 'src/registry/components/table/cells/CellBoolean';
import { billingMessages } from 'src/features/billing/utils/messages';
import CreateBillingEntityBankAccountFormModal from 'src/features/billingEntityBankAccounts/components/forms/CreateBillingEntityBankAccountFormModal';
import CellBillingEntityBankAccountActions from 'src/features/billingEntityBankAccounts/components/tables/CellBillingEntityBankAccountActions';
import { booleanBadgeOptions } from 'src/utils/constants';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';

const BillingEntityBankAccountsTable = () => {
    const { formatMessage } = useIntl();
    const { entityId = '' } = useParams();
    const permissionManageBillingEntity = useHasPermissions([PERMISSION.MANAGE_BILLING_ENTITIES]);
    const permissionAccessBillingEntity = useHasPermissions([PERMISSION.ACCESS_BILLING_ENTITIES]);

    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'name', desc: false }],
    });
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { queryFiltersOptions, ...filters } = useFilters();

    const {
        data: bankAccounts,
        isLoading,
        isError,
    } = useListBillingEntityBankAccounts({
        ...(entityId && { billing_entity: entityId }),
        ...querySearchOptions,
        ...querySortingOptions,
        ...queryPaginationOptions,
        ...queryFiltersOptions,
    });

    const columnHelper = createColumnHelper<BankAccount>();

    const columns = [
        columnHelper.accessor('name', {
            enableSorting: true,
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(commonMessages.name),
            },
        }),
        ...(!entityId
            ? [
                  columnHelper.accessor('billing_entity.name', {
                      enableSorting: true,
                      header: DataTableColumnHeader,
                      enableHiding: true,
                      cell: (ctx) => {
                          const entityId = ctx.row.original.billing_entity?.id;
                          return (
                              <CellLinkWithIcon
                                  {...ctx}
                                  to={`/billing-entity/${entityId}`}
                                  hasPermissions={permissionAccessBillingEntity}
                              />
                          );
                      },
                      meta: {
                          title: formatMessage(billingMessages.billingEntity),
                      },
                  }),
              ]
            : []),
        columnHelper.accessor('iban', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(bankAccountMessages.iban),
            },
        }),
        columnHelper.accessor('bic', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(bankAccountMessages.bic),
            },
        }),
        columnHelper.accessor('currency', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                return <CellCurrencyBadge currency={row.original?.currency} />;
            },
            meta: {
                title: formatMessage(commonMessages.currency),
            },
        }),
        columnHelper.accessor('is_active', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBoolean,
            meta: {
                title: formatMessage(moneyAccountsMessages.isActive),
                badgeOptions: booleanBadgeOptions,
            },
        }),
        ...(permissionManageBillingEntity
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: CellBillingEntityBankAccountActions,
                      maxSize: 45,
                  }),
              ]
            : []),
    ];

    return (
        <DataTable<BankAccount, unknown>
            columns={columns}
            data={bankAccounts}
            semantics={{
                title: formatMessage(bankAccountMessages.bankAccounts),
                entryName: formatMessage(moneyAccountsMessages.account).toLowerCase(),
                entryNames: formatMessage(moneyAccountsMessages.accounts).toLowerCase(),
                placeholder: formatMessage(moneyAccountsMessages.account).toLowerCase(),
            }}
            search={search}
            sorting={sorting}
            pagination={pagination}
            filters={filters}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(permissionManageBillingEntity && { newEntry: CreateBillingEntityBankAccountFormModal }),
            }}
        />
    );
};

export default BillingEntityBankAccountsTable;
